<template>
  <div class="follow-up-statement-selector">
    <label class="field-label">Follow Up statement*</label>
    
    <!-- Dropdown with custom items -->
    <v-select
      v-model="selectedStatementId"
      :items="statementItems"
      variant="outlined"
      density="compact"
      hide-details
      item-title="displayTitle"
      item-value="id"
      class="follow-up-statement-select"
      @update:modelValue="onSelectionChange"
    >
      <!-- Custom item template with edit icons -->
      <template v-slot:item="{ item, props }">
        <v-list-item v-bind="props" class="statement-item">
          <template v-slot:append v-if="item.raw.isCustom">
            <div class="item-actions">
              <v-btn
                size="small"
                variant="text"
                color="primary"
                @click.stop="editStatement(item.raw)"
                class="edit-btn"
              >
                <img src="/assets/edit.png" alt="Edit" class="edit-icon" />
              </v-btn>
            </div>
          </template>
        </v-list-item>
      </template>

      <!-- Fixed "Add Custom" button at bottom -->
      <template v-slot:append-item>
        <v-list-item
          class="add-custom-item"
          @click="openCreateDialog"
        >
          <template v-slot:prepend>
            <v-icon color="#000000">mdi-plus</v-icon>
          </template>
          <v-list-item-title class="add-custom-text">Add Custom</v-list-item-title>
        </v-list-item>
      </template>
    </v-select>

    <!-- Create/Edit Dialog -->
    <BaseDialog
      :visible="showCreateDialog"
      :config="createDialogConfig"
      :isValidForSubmit="isValid"
      @close="closeCreateDialog"
      @action="saveStatement"
    >
      <p class="text-subtitle-2 mb-4">
        Create a custom follow up statement to send to the client
      </p>

      <!-- Follow Up Name Field -->
      <div class="dialog-field-container">
        <label class="dialog-field-label">Statement Name*</label>
        <v-text-field
          v-model="dialogData.name"
          placeholder="List Name"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
        ></v-text-field>
        <div class="field-hint" :class="{ 'error': dialogData.name && dialogData.name.length > 20 }">
          {{ dialogData.name ? dialogData.name.length : 0 }}/20 characters
        </div>
      </div>

      <!-- Follow Up Content Field -->
      <div class="dialog-field-container">
        <label class="dialog-field-label">Statement*</label>
        <div class="follow-up-content-container">
          <VueEditor
            ref="followUpContentEditor"
            key="followUpContentEditor"
            class="follow-up-content-vue-editor"
            v-model="dialogData.content"
            :editorToolbar="customToolbar"
            :editorOptions="editorOptions"
            placeholder="Additional comments"
            @input="onFollowUpContentInput"
            @text-change="onFollowUpContentTextChange"
          />
          
          <!-- Character Count -->
          <div class="follow-up-content-char-count" :class="{ error: followUpContentLength > 200 }">
            {{ followUpContentLength }}/200 characters
          </div>
        </div>
      </div>

      <!-- Delete button for editing (placed in content area) -->
      <div v-if="isEditing" class="delete-section">
        <v-btn
          variant="outlined"
          color="#003f2d"
          @click="deleteCurrentStatement"
          :loading="isDeleting"
          class="delete"
        >
          <img src="/assets/delete.png" alt="Delete" class="delete-icon" />
          Delete
        </v-btn>
      </div>
    </BaseDialog>

    <!-- Delete Confirmation Dialog -->
    <BaseDialog
      :visible="showDeleteDialog"
      :config="deleteDialogConfig"
      :isValidForSubmit="true"
      @close="showDeleteDialog = false"
      @action="confirmDelete"
    >
      <p class="text-body-1 mb-3">
        Are you sure you want to delete "<strong>{{ statementToDelete?.name }}</strong>"?
      </p>
      <p class="text-body-2 text-medium-emphasis">
        This action cannot be undone.
      </p>
    </BaseDialog>
  </div>
</template>

<script>
import BaseDialog from '../commons/BaseDialog.vue';
import { VueEditor } from 'vue3-editor';
import {
  initializeFollowUpStatementService,
  getAllFollowUpStatements,
  createCustomFollowUpStatement,
  updateCustomFollowUpStatement,
  deleteCustomFollowUpStatement,
  getSelectedStatementId,
  setSelectedStatementId,
  getWordCount
} from '../../service/followUpStatementService.js';

export default {
  name: 'FollowUpStatementSelector',
  
  components: {
    BaseDialog,
    VueEditor
  },
  
  props: {
    modelValue: {
      type: String,
      default: 'default'
    },
    context: {
      type: String,
      default: 'client',
      validator: (value) => ['client', 'property'].includes(value)
    }
  },

  emits: ['update:modelValue', 'statement-changed'],

  data() {
    return {
      selectedStatementId: 'default',
      allStatements: [],
      showCreateDialog: false,
      showDeleteDialog: false,
      isEditing: false,
      isLoading: false,
      isSaving: false,
      isDeleting: false,
      
      dialogData: {
        id: null,
        name: '',
        content: ''
      },
      
      dialogErrors: {
        name: [],
        content: []
      },
      
      statementToDelete: null,
      wordCount: 0,
      
      // VueEditor configuration
      customToolbar: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link'],
        ['clean']
      ],
      
      editorOptions: {
        debug: false,
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link'],
            ['clean']
          ],
          history: {
            delay: 1000,
            maxStack: 50,
            userOnly: false
          },
          clipboard: {
            matchVisual: false
          }
        },
        theme: 'snow',
        strict: false,
        scrollingContainer: null,
        bounds: null,
        readOnly: false,
      }
    };
  },

  computed: {
    statementItems() {
      return this.allStatements.map(statement => ({
        ...statement,
        displayTitle: statement.name,
        // Add visual indicator for custom statements
        title: statement.isCustom ? `${statement.name} (Custom)` : statement.name
      }));
    },

    isValid() {
      // Ensure we have the dialog data
      if (!this.dialogData) return false;
      
      // Check name: must have actual text and not exceed length
      const nameText = String(this.dialogData.name || '').trim();
      if (!nameText || nameText.length > 20) return false;
      
      // Check content: must have actual text content (not just HTML markup) and not exceed limit
      const contentLength = this.followUpContentLength;
      if (contentLength <= 0 || contentLength > 200) return false;
      
      return true;
    },

    followUpContentLength() {
      if (!this.dialogData.content) return 0;
      
      try {
        // Get plain text length (strip HTML tags)
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = this.dialogData.content || '';
        const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
        return textContent.length;
      } catch (error) {
        console.warn('Error calculating follow-up content length:', error);
        return 0;
      }
    },

    createDialogConfig() {
      return {
        title: `Custom Follow Up Statement`,
        label: this.isEditing ? 'Save Changes' : 'Add Statement',
        needCanacel: true,
        hasIcon: false,
        isSelectAssignment: false
      };
    },

    deleteDialogConfig() {
      return {
        title: 'Confirm Delete',
        label: 'Delete',
        needCanacel: true,
        hasIcon: false,
        isSelectAssignment: false
      };
    }
  },

  async mounted() {
    await this.initializeComponent();
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(newValue) {
        if (newValue && newValue !== this.selectedStatementId) {
          this.selectedStatementId = newValue;
          console.log(`📝 FollowUpStatementSelector modelValue changed to: ${newValue}`);
        }
      }
    },
    context: {
      immediate: false,
      handler() {
        // Reload statements when context changes
        this.initializeComponent();
      }
    }
  },

  methods: {
    async initializeComponent() {
      try {
        this.isLoading = true;
        
        // Initialize the service with context
        await initializeFollowUpStatementService(this.context);
        
        // Load all statements
        await this.loadStatements();
        
        // Use modelValue if provided, otherwise load from storage
        if (this.modelValue) {
          this.selectedStatementId = this.modelValue;
          console.log(`📝 Using modelValue for initial selection: ${this.modelValue}`);
        } else {
          // Set initial selection from storage
          const selectedId = await getSelectedStatementId(this.context);
          this.selectedStatementId = selectedId || 'default';
          console.log(`📝 Using stored selection: ${this.selectedStatementId}`);
        }
        
        // Emit initial value only if it differs from modelValue
        if (this.selectedStatementId !== this.modelValue) {
          this.$emit('update:modelValue', this.selectedStatementId);
        }
        
      } catch (error) {
        console.error('Error initializing follow-up statement selector:', error);
        this.$emit('error', 'Failed to load follow-up statements');
      } finally {
        this.isLoading = false;
      }
    },

    async loadStatements() {
      try {
        this.allStatements = await getAllFollowUpStatements(this.context);
        console.log(`📋 Loaded follow-up statements for ${this.context}:`, this.allStatements.length);
      } catch (error) {
        console.error('Error loading statements:', error);
        this.allStatements = [];
      }
    },

    async onSelectionChange(statementId) {
      try {
        if (statementId) {
          // Save selection with context
          await setSelectedStatementId(statementId, this.context);
          
          // Emit changes
          this.$emit('update:modelValue', statementId);
          
          // Find the selected statement and emit it
          const selectedStatement = this.allStatements.find(s => s.id === statementId);
          if (selectedStatement) {
            this.$emit('statement-changed', selectedStatement);
          }
          
          console.log('✅ Selected follow-up statement:', statementId);
        }
      } catch (error) {
        console.error('Error changing statement selection:', error);
      }
    },

    openCreateDialog() {
      this.isEditing = false;
      this.dialogData = {
        id: null,
        name: '',
        content: ''
      };
      this.clearErrors();
      this.updateWordCount();
      this.showCreateDialog = true;
    },

    editStatement(statement) {
      this.isEditing = true;
      this.dialogData = {
        id: statement.id,
        name: statement.name,
        content: statement.content
      };
      this.clearErrors();
      this.updateWordCount();
      this.showCreateDialog = true;
    },

    deleteStatement(statement) {
      this.statementToDelete = statement;
      this.showDeleteDialog = true;
    },

    closeCreateDialog() {
      this.showCreateDialog = false;
      this.dialogData = { id: null, name: '', content: '' };
      this.clearErrors();
      this.wordCount = 0;
    },

    clearErrors() {
      this.dialogErrors = {
        name: [],
        content: []
      };
    },

    // Clear specific field errors
    clearFieldError(fieldName) {
      if (this.dialogErrors[fieldName]) {
        this.dialogErrors[fieldName] = [];
      }
    },

    updateWordCount() {
      this.wordCount = getWordCount(this.dialogData.content);
    },

    onFollowUpContentInput(content) {
      try {
        this.dialogData.content = content || '';
      } catch (error) {
        console.warn('Error in follow-up content input handler:', error.message);
        if (content) {
          this.dialogData.content = content;
        }
      }
    },

    onFollowUpContentTextChange() {
      try {
        // This method is called when the editor content changes
        // The content is automatically bound to dialogData.content via v-model
        this.updateWordCount();
      } catch (error) {
        console.warn('Error in follow-up content text change handler:', error.message);
      }
    },

    async saveStatement() {
      try {
        this.isSaving = true;

        let savedStatement;
        if (this.isEditing) {
          // Update existing statement
          savedStatement = await updateCustomFollowUpStatement(
            this.dialogData.id,
            this.dialogData.name,
            this.dialogData.content,
            this.context
          );
          console.log('✅ Updated statement:', savedStatement.name);
        } else {
          // Create new statement
          savedStatement = await createCustomFollowUpStatement(
            this.dialogData.name,
            this.dialogData.content,
            this.context
          );
          console.log('✅ Created new statement:', savedStatement.name);
        }

        // Reload statements and select the new/updated one
        await this.loadStatements();
        await this.onSelectionChange(savedStatement.id);

        this.closeCreateDialog();
        this.$emit('success', `Follow-up statement ${this.isEditing ? 'updated' : 'created'} successfully`);

      } catch (error) {
        console.error('Error saving statement:', error);
        this.$emit('error', error.message || 'Failed to save follow-up statement');
      } finally {
        this.isSaving = false;
      }
    },

    deleteCurrentStatement() {
      if (this.dialogData.id) {
        this.statementToDelete = this.allStatements.find(s => s.id === this.dialogData.id);
        this.showDeleteDialog = true;
      }
    },

    async confirmDelete() {
      try {
        if (!this.statementToDelete) return;

        this.isDeleting = true;

        await deleteCustomFollowUpStatement(this.statementToDelete.id, this.context);
        console.log('✅ Deleted statement:', this.statementToDelete.name);

        // Reload statements
        await this.loadStatements();

        // Refresh selected statement (it might have been reset to default)
        const selectedId = await getSelectedStatementId(this.context);
        this.selectedStatementId = selectedId || 'default';
        
        // Emit the new selection
        this.$emit('update:modelValue', this.selectedStatementId);
        
        // Find and emit the selected statement
        const selectedStatement = this.allStatements.find(s => s.id === this.selectedStatementId);
        if (selectedStatement) {
          this.$emit('statement-changed', selectedStatement);
        }

        // Close dialogs
        this.showDeleteDialog = false;
        this.showCreateDialog = false;

        // Reset dialog data
        this.dialogData = { id: null, name: '', content: '' };
        this.statementToDelete = null;

        this.$emit('success', 'Follow-up statement deleted successfully');

      } catch (error) {
        console.error('Error deleting statement:', error);
        this.$emit('error', error.message || 'Failed to delete follow-up statement');
      } finally {
        this.isDeleting = false;
      }
    }
  }
};
</script>

<style scoped>
/* Component-specific styles */
.follow-up-statement-selector {
  margin-bottom: 1rem;
}

/* Ensure the select dropdown has exact height with no gaps */
.follow-up-statement-select :deep(.v-overlay__content) {
  height: 300px !important;
  max-height: 300px !important;
  min-height: 300px !important;
  overflow: hidden !important;
  padding: 0 !important;
  margin: 0 !important;
  border: 1px solid #ccc;
}

/* Force exact container dimensions */
.follow-up-statement-select :deep(.v-menu > .v-overlay__content) {
  height: 300px !important;
  max-height: 300px !important;
}

/* Create exact container structure with no gaps */
.follow-up-statement-select :deep(.v-select__content) {
  padding: 0 !important;
  margin: 0 !important;
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
}

.follow-up-statement-select :deep(.v-card) {
  padding: 0 !important;
  margin: 0 !important;
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Style the list container with exact scrollable area */
.follow-up-statement-select :deep(.v-list) {
  padding: 0 !important;
  margin: 0 !important;
  overflow-y: auto !important;
  flex: 1 !important;
  height: calc(300px - 60px) !important;
  max-height: calc(300px - 60px) !important;
  min-height: calc(300px - 60px) !important;
}

/* Ensure list items have no gaps */
.follow-up-statement-select :deep(.v-list-item) {
  padding: 8px 16px !important;
  margin: 0 !important;
  border-bottom: 1px solid #f0f0f0;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  color: #000;
  font-size: 16px;
}

.statement-item {
  position: relative;
}

.item-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s;
}

.statement-item:hover .item-actions {
  opacity: 1;
}

.edit-icon {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.add-custom-item {
  position: sticky !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 10 !important;
  gap: 32px;
  background-color: white !important;
  min-height: 50px !important;
  height: 50px !important;
  padding: 16px !important;
  margin: 0 !important;
  display: flex !important;
  align-items: center !important;
  width: 100% !important;
}

/* Remove the ::after pseudo-element as it's no longer needed */
.add-custom-item::before {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  right: 0;
  height: 10px;
  background-color: white;
  z-index: 11;
}

.add-custom-text {
  color: #000000;
}

.add-custom-item:hover {
  background-color: #e8f5e8 !important;
}

/* Dialog content styles */
.word-count-info {
  text-align: right;
  margin-top: -8px;
  margin-bottom: 16px;
}

/* Dropdown styles - Fix entire container to prevent text bleeding */
.follow-up-statement-select :deep(.v-list) {
  max-height: 300px;
  overflow-y: auto;
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
  position: relative;
}

/* Fix the overall dropdown menu container */
.follow-up-statement-select :deep(.v-menu > .v-overlay__content > .v-card) {
  overflow: hidden !important;
  max-height: 300px !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Ensure the select content area is properly contained */
.follow-up-statement-select :deep(.v-select__content) {
  max-height: 300px !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Fix the list container to prevent overflow */
.follow-up-statement-select :deep(.v-list) {
  flex: 1 1 auto !important;
  overflow-y: auto !important;
  max-height: 240px !important; /* Leave 60px for Add Custom button */
}

/* Ensure the append item area is properly positioned */
.follow-up-statement-select :deep(.v-list-group) {
  margin-bottom: 0 !important;
}

/* Ensure Add Custom button stays at bottom and fills remaining space */
.follow-up-statement-select :deep(.v-list-item--append-item) {
  position: sticky;
  bottom: 0;
  background: white !important;
  z-index: 4;
  min-height: 60px !important;
  height: 60px !important;
  padding: 16px !important;
  margin-bottom: 0 !important;
  border-top: 2px solid #e0e0e0;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
  display: flex !important;
  align-items: center !important;
}

/* Remove any padding/margin from the dropdown content container */
.follow-up-statement-select :deep(.v-select__content) {
  overflow: hidden !important;
  padding-bottom: 0 !important;
  max-height: 300px !important;
  display: flex !important;
  flex-direction: column !important;
}

.follow-up-statement-select :deep(.v-select__content .v-list) {
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
  flex: 1 1 auto !important;
  max-height: 240px !important;
}

/* Ensure the dropdown menu itself has no bottom spacing and proper height */
.follow-up-statement-select :deep(.v-overlay__content) {
  padding-bottom: 0 !important;
  max-height: 300px !important;
  overflow: hidden !important;
}

/* Fix the Vuetify menu wrapper */
.follow-up-statement-select :deep(.v-menu > .v-overlay__content) {
  overflow: hidden !important;
  max-height: 300px !important;
}

/* Ensure proper container for the entire select */
.follow-up-statement-select :deep(.v-field) {
  position: relative;
}

/* Fix any default Vuetify card padding in dropdown */
.follow-up-statement-select :deep(.v-card) {
  padding: 0 !important;
  margin: 0 !important;
  max-height: 300px !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Remove all default padding from v-select components */
.follow-up-statement-select :deep(.v-select .v-menu) {
  padding: 0 !important;
}

.follow-up-statement-select :deep(.v-menu .v-list) {
  padding: 0 !important;
  margin: 0 !important;
}

/* Target the specific menu content wrapper */
.follow-up-statement-select :deep(.v-menu__content) {
  padding: 0 !important;
  margin: 0 !important;
  max-height: 300px !important;
  overflow: hidden !important;
}

/* Remove padding from overlay scrim */
.follow-up-statement-select :deep(.v-overlay__scrim) {
  padding: 0 !important;
}

/* Ensure list group has no spacing */
.follow-up-statement-select :deep(.v-list-group__items) {
  padding: 0 !important;
}

/* Remove any theme-specific padding */
.follow-up-statement-select :deep(.v-theme--light) {
  padding: 0 !important;
}

/* Force the entire dropdown menu to have no spacing */
.follow-up-statement-select :deep(*) {
  box-sizing: border-box;
}

.follow-up-statement-select :deep(.v-menu),
.follow-up-statement-select :deep(.v-overlay),
.follow-up-statement-select :deep(.v-overlay__content),
.follow-up-statement-select :deep(.v-card),
.follow-up-statement-select :deep(.v-list),
.follow-up-statement-select :deep(.v-select__content) {
  padding: 0 !important;
  margin: 0 !important;
}

/* Ensure list items don't bleed through */
.follow-up-statement-select :deep(.v-list-item) {
  position: relative;
  z-index: 1;
}

/* Force remove any default Vuetify spacing */
.follow-up-statement-select :deep(.v-list),
.follow-up-statement-select :deep(.v-list-item-group),
.follow-up-statement-select :deep(.v-menu__content),
.follow-up-statement-select :deep(.v-select__content) {
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}

/* Ensure the entire dropdown area has no gaps */
.follow-up-statement-select :deep(.v-field__input) {
  padding-bottom: 0 !important;
}

::v-deep .v-list-item__prepend {
  width: 0px !important;
}

/* Dialog field styling */
.dialog-field-container {
  margin-bottom: 1rem;
}

.dialog-field-label {
  display: block;
  margin-bottom: 8px;
  color: #000;
  font-size: 14px;
  font-weight: 500;
}

.field-hint {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  text-align: right;
}

.field-hint.error {
  color: #d32f2f;
  font-weight: 500;
}

/* Delete button styling */
.delete-section {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.delete-icon {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  object-fit: contain;
}
.delete {
  padding: 4px 8px 4px 8px;
  text-transform: capitalize;
}

/* VueEditor styling for follow-up content */
.follow-up-content-container {
  position: relative;
}

.follow-up-content-vue-editor {
  min-height: 150px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.follow-up-content-vue-editor ::v-deep .ql-editor {
  min-height: 120px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
}

.follow-up-content-vue-editor ::v-deep .ql-toolbar {
  border-bottom: 1px solid #e0e0e0;
  font-family: inherit;
}

.follow-up-content-char-count {
  position: absolute;
  bottom: -20px;
  right: 0;
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.follow-up-content-char-count.error {
  color: #d32f2f;
  font-weight: 500;
}

.error-message {
  color: #d32f2f;
  font-size: 12px;
  margin-top: 4px;
}
</style>
